import { mean, stdDev, rollingMean } from './stats';

/**
 * Least-squares slope of arr (treated as y at x = 0..n-1). Returns 0 for length < 2.
 * @param {number[]} arr
 * @returns {number}
 */
export function slope(arr) {
  if (!Array.isArray(arr) || arr.length < 2) return 0;
  const ys = arr.filter((x) => Number.isFinite(x));
  if (ys.length < 2) return 0;
  const xs = ys.map((_, i) => i);
  const xm = (xs.length - 1) / 2;
  const ym = ys.reduce((s, y) => s + y, 0) / ys.length;
  let num = 0;
  let den = 0;
  for (let i = 0; i < xs.length; i++) {
    num += (xs[i] - xm) * (ys[i] - ym);
    den += (xs[i] - xm) ** 2;
  }
  return den === 0 ? 0 : num / den;
}

/**
 * Classifies the shape of arr into one of:
 * - 'rising'   — clear upward trend (slope > sd/n threshold)
 * - 'falling'  — clear downward trend
 * - 'volatile' — high variance relative to mean
 * - 'flat'     — none of the above
 *
 * Designed for Sparkline auto-coloring (PRD-69 §3.2.1): callers map
 * 'rising' → 'worsening' for risk scores (where higher is worse per PRD-51).
 * @param {number[]} arr
 * @returns {'rising'|'falling'|'volatile'|'flat'}
 */
export function classifyShape(arr) {
  if (!Array.isArray(arr) || arr.length < 2) return 'flat';
  const ys = arr.filter((x) => Number.isFinite(x));
  if (ys.length < 2) return 'flat';

  const m = mean(ys);
  const sd = stdDev(ys);
  const s = slope(ys);
  const range = Math.max(...ys) - Math.min(...ys);

  // Slope threshold: slope per step expressed as fraction of total range.
  // Anything beyond 5% of range per step counts as a directional trend.
  const slopeThreshold = range === 0 ? 0 : range * 0.05;

  if (Math.abs(s) >= slopeThreshold && s > 0) return 'rising';
  if (Math.abs(s) >= slopeThreshold && s < 0) return 'falling';

  // Volatility: stddev/mean coefficient of variation > 25%, or range > 50% of mean.
  if (Math.abs(m) > Number.EPSILON) {
    const cv = sd / Math.abs(m);
    if (cv > 0.25 || range / Math.abs(m) > 0.5) return 'volatile';
  }

  return 'flat';
}

/**
 * Returns an array of { upper, lower } bounds per index using a trailing rolling-mean
 * center ± sigma * trailing-stddev. Useful for highlighting anomalies on a Sparkline.
 * @param {number[]} arr
 * @param {number} [window=7]
 * @param {number} [sigma=2]
 * @returns {{ upper: number[], lower: number[] }}
 */
export function anomalyBand(arr, window = 7, sigma = 2) {
  if (!Array.isArray(arr) || arr.length === 0) return { upper: [], lower: [] };
  const w = Math.max(1, Math.floor(window));
  const center = rollingMean(arr, w);
  const upper = new Array(arr.length);
  const lower = new Array(arr.length);
  for (let i = 0; i < arr.length; i++) {
    const start = Math.max(0, i - w + 1);
    const slice = arr.slice(start, i + 1).filter((x) => Number.isFinite(x));
    const sd = stdDev(slice);
    const c = center[i];
    if (!Number.isFinite(c) || !Number.isFinite(sd)) {
      upper[i] = NaN;
      lower[i] = NaN;
    } else {
      upper[i] = c + sigma * sd;
      lower[i] = c - sigma * sd;
    }
  }
  return { upper, lower };
}
